// Shared helpers for the two frontend image-upload hooks
// (`useAvatarUpload` for personal avatars and `useCrestUpload`
// for the family crest). Both walk the same path:
//
//   validate (mime + size)
//   → in-browser resize to a webp blob up to MAX_IMAGE_EDGE_PX
//   → upload to the target bucket at a deterministic object path
//   → write the resulting public URL back to the relevant DB row
//
// What differs between the two hooks is only the bucket name, the
// object-path convention, and the DB row to update — none of which
// lives here. Centralising the validation, decompression-resistant
// pipeline, error-message mapping, and random-suffix helper keeps
// both hooks small and ensures the user-visible error strings match.

/** 5 MiB cap matches both the `avatars` and `family-crests` bucket
 *  policies (migration 013 + 04980). Anything bigger is rejected
 *  at the input step so the user gets fast feedback instead of
 *  waiting through a long compression run only to have the upload
 *  fail. */
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

/** Side length applied to the longest edge before re-encoding.
 *  512px is more than enough for a circular avatar (the largest
 *  rendering at ~120px) or the crest chip in chat headers
 *  (~40px). Going wider only consumes storage with no visible
 *  fidelity gain. */
export const MAX_IMAGE_EDGE_PX = 512;

/** Compress / resize the picked image to a webp blob. Returns the
 *  original `File` unchanged if the browser can't run canvas, so
 *  the caller's upload step sees the same bytes either way. */
export async function prepareImage(file: File): Promise<Blob> {
  if (typeof document === "undefined") return file;
  try {
    const decoded = await decodeImage(file);
    const ratio = Math.min(1, MAX_IMAGE_EDGE_PX / Math.max(decoded.width, decoded.height));
    const width = Math.max(1, Math.round(decoded.width * ratio));
    const height = Math.max(1, Math.round(decoded.height * ratio));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(decoded.bitmap, 0, 0, width, height);
    const blob = await canvasToBlob(canvas, "image/webp", 0.82);
    decoded.dispose();
    // Only swap to the compressed form if it actually got smaller;
    // a 6 KiB PNG won't shrink under webp re-encode, and we don't
    // want to regress fidelity for no storage savings.
    if (blob && blob.size > 0 && blob.size < file.size) {
      return blob;
    }
    return file;
  } catch {
    // Canvas / bitmap failure (e.g. HEIC without a decoder) — fall
    // back to the original bytes. If those also fail to upload, the
    // friendlyUploadErr message below will surface.
    return file;
  }
}

type DecodedImage = {
  bitmap: CanvasImageSource;
  width: number;
  height: number;
  dispose: () => void;
};

async function decodeImage(file: File): Promise<DecodedImage> {
  if (typeof createImageBitmap === "function") {
    const bitmap = await createImageBitmap(file);
    return {
      bitmap,
      width: bitmap.width,
      height: bitmap.height,
      dispose: () => bitmap.close(),
    };
  }
  // Fallback for older browsers. HTMLImageElement cannot be reliably
  // closed; we just revoke the object URL we created.
  const url = URL.createObjectURL(file);
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error("unable to decode image"));
    el.src = url;
  });
  return {
    bitmap: img,
    width: img.naturalWidth,
    height: img.naturalHeight,
    dispose: () => URL.revokeObjectURL(url),
  };
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), type, quality);
  });
}

/** Short random hex used as the trailing segment of upload paths so
 *  the bucket path is unique even when two uploads happen in the
 *  same millisecond. crypto.randomUUID is preferred; the
 *  Math.random fallback is here for very old browsers that the
 *  Cropper pipeline already supports. */
export function randSuffix(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID().slice(0, 8);
  }
  return Math.floor(Math.random() * 0xffffffff).toString(16).padStart(8, "0");
}

/** Map a Supabase Storage error string to something a non-technical
 *  user can act on. Shared between avatar + crest uploads because
 *  the upstream error vocabulary is identical (the bucket policies
 *  raise `mime type`, `exceeded the maximum allowed size`, and
 *  `row-level security` / `permission denied`). */
export function friendlyUploadErr(msg: string, opts?: { asset?: "頭像" | "家徽" }): string {
  const asset = opts?.asset ?? "圖片";
  const m = msg.toLowerCase();
  if (m.includes("exceeded") || m.includes("size") || m.includes("limit"))
    return `${asset}超過 5 MiB 上限，請先壓縮或選擇較小的${asset}。`;
  if (m.includes("mime") || m.includes("type"))
    return `不支援嘅${asset}格式，請使用 PNG、JPEG 或 WebP。`;
  if (m.includes("row-level security") || m.includes("permission"))
    return `你冇權限寫入呢個${asset}，請重新登入。`;
  return `${asset}上傳失敗：${msg}`;
}
